import React, { createContext, useContext, useEffect, useState } from 'react';
import api from '../../utils/api';
import AppContext from '../../contexts/AppContext';
import { USER_STATES } from '../../utils/constants';
import { FEATURE_NAMES, FeatureName } from './feature-toggle.constants';
import { EvaluateResponse } from './types';

// Dev override: set REACT_APP_FORCE_MOOP_PHASE_II=true to enable Phase II locally without a live toggle.
const DEV_FORCE_PHASE_II =
  process.env.NODE_ENV !== 'production' && process.env.REACT_APP_FORCE_MOOP_PHASE_II === 'true';

interface ToggleState {
  isEnabled: boolean;
  isLoading: boolean;
}

type FeatureToggleMap = Record<string, ToggleState>;

const FeatureToggleContext = createContext<FeatureToggleMap>({});

interface FeatureToggleProviderProps {
  children: React.ReactNode;
}

export const FeatureToggleProvider: React.FC<FeatureToggleProviderProps> = ({ children }) => {
  const { user } = useContext(AppContext);
  const [toggles, setToggles] = useState<FeatureToggleMap>({
    [FEATURE_NAMES.MOOP_PHASE_II]: { isEnabled: DEV_FORCE_PHASE_II, isLoading: !DEV_FORCE_PHASE_II },
  });

  useEffect(() => {
    if (DEV_FORCE_PHASE_II) return;

    // Wait until user is resolved (not authenticating / not_authenticated)
    if (!user || user === USER_STATES.AUTHENTICATING || user === USER_STATES.NOT_AUTHENTICATED) return;

    const requester = typeof user === 'object' ? user.email : undefined;

    api
      .evaluateFeatureToggle(FEATURE_NAMES.MOOP_PHASE_II, { requester })
      .then((response: { data: { data: EvaluateResponse } }) => {
        const enabled = response?.data?.data?.enabled ?? false;
        setToggles((prev) => ({
          ...prev,
          [FEATURE_NAMES.MOOP_PHASE_II]: { isEnabled: enabled, isLoading: false },
        }));
      })
      .catch(() => {
        // Safe default: hide Phase II on error
        setToggles((prev) => ({
          ...prev,
          [FEATURE_NAMES.MOOP_PHASE_II]: { isEnabled: false, isLoading: false },
        }));
      });
  }, [user]);

  return <FeatureToggleContext.Provider value={toggles}>{children}</FeatureToggleContext.Provider>;
};

export const useFeatureToggle = (flagName: FeatureName): ToggleState => {
  const toggles = useContext(FeatureToggleContext);
  return toggles[flagName] ?? { isEnabled: false, isLoading: false };
};
